<template>
  <!-- noGrid: rendera utan egen v-row/v-col (perfekt när komponenten ligger i en md=6-kolumn redan) -->
  <div v-if="noGrid">
    <div v-for="(cfg, inx) in chartModels" :key="inx" class="pa-2">
      <v-card outlined class="pa-2 h-100 d-flex flex-column">
        <h3
          class="ma-4"
          style="
            font-size: clamp(12px, 2vw, 18px);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
        >
          {{ cfg.heading }}
        </h3>

        <div
          v-if="cfg.format === 'SingleNumeric' || cfg.format === 'integer'"
          class="charts"
        >
          <ApexChart
            :key="cfg.chartOptions.chart.id"
            :type="cfg.type"
            :options="cfg.chartOptions"
            :series="cfg.series"
            width="100%"
            :height="300"
          />
          <div class="mt-2">Totalt antal svar {{ cfg.pointCount }}</div>
        </div>

        <div v-else-if="cfg.format === 'MultiNumeric'">
          <ApexChart
            :key="cfg.chartOptions.chart.id"
            :type="cfg.type"
            :options="cfg.chartOptions"
            :series="cfg.series"
            width="100%"
            :height="300"
          />
          <div class="mt-2">Antal hanterade svar {{ cfg.pointCount }}</div>
        </div>
      </v-card>
    </div>
  </div>

  <!-- Standard: egen grid, två i bredd -->
  <v-row v-else no-gutters>
    <v-col v-for="(cfg, inx) in chartModels" :key="inx" cols="12" md="6" class="pa-2">
      <v-card outlined class="pa-2 h-100 d-flex flex-column">
        <h3
          class="ma-4"
          style="
            font-size: clamp(12px, 2vw, 18px);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
        >
          {{ cfg.heading }}
        </h3>

        <div
          v-if="cfg.format === 'SingleNumeric' || cfg.format === 'integer'"
          class="charts"
        >
          <ApexChart
            :key="cfg.chartOptions.chart.id"
            :type="cfg.type"
            :options="cfg.chartOptions"
            :series="cfg.series"
            width="100%"
            :height="300"
          />
          <div class="mt-2">Totalt antal svar {{ cfg.pointCount }}</div>
        </div>

        <div v-else-if="cfg.format === 'MultiNumeric'">
          <ApexChart
            :key="cfg.chartOptions.chart.id"
            :type="cfg.type"
            :options="cfg.chartOptions"
            :series="cfg.series"
            width="100%"
            :height="300"
          />
          <div class="mt-2">Antal hanterade svar {{ cfg.pointCount }}</div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";

// ——— Helpers (self‑contained) ———
function deepMerge(base, override) {
  const out = Array.isArray(base) ? base.slice() : { ...base };
  for (const k in override || {}) {
    const v = override[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = deepMerge(base?.[k] || {}, v);
    } else {
      out[k] = Array.isArray(v) ? v.slice() : v;
    }
  }
  return out;
}

function normalizeYAxis(yaxis) {
  const levels = yaxis?.labels?.levels || null;
  if (!levels) return yaxis;
  const parsedLevels = Object.fromEntries(
    Object.entries(levels).map(([k, v]) => [parseInt(k, 10), v])
  );
  const keys = Object.keys(parsedLevels).map(Number);
  const min = Math.min(...keys);
  const max = Math.max(...keys);
  return {
    ...yaxis,
    min,
    max,
    tickAmount: max - min, // steps, not labels count
    forceNiceScale: false,
    labels: {
      ...yaxis.labels,
      formatter(val) {
        const key = Math.round(val);
        return parsedLevels[key] ?? "";
      },
    },
  };
}

function makeAverageSeries(series) {
  const cleaned = series.map((s) => ({
    ...s,
    data: s.data.filter((p) => p.y !== 0 && p.y !== null),
  }));
  const pointCount = cleaned.reduce((sum, s) => sum + s.data.length, 0);
  const grouped = {};
  cleaned.forEach((s) => {
    s.data.forEach((p) => {
      if (!grouped[p.x]) grouped[p.x] = [];
      grouped[p.x].push(p.y);
    });
  });
  const avgData = Object.entries(grouped).map(([x, values]) => ({
    x,
    y: values.reduce((a, b) => a + b, 0) / values.length,
  }));
  return {
    cleaned,
    average: { name: "Medelvärde", data: avgData },
    pointCount,
  };
}

function addCountSeries(series) {
  const grouped = {};
  series.forEach((s) => {
    s.data.forEach((p) => {
      if (p.y !== 0 && p.y !== null) {
        if (!grouped[p.x]) grouped[p.x] = 0;
        grouped[p.x] += 1;
      }
    });
  });
  const countData = Object.entries(grouped).map(([x, count]) => ({ x, y: count }));
  return {
    name: "Antal svar vid varje tidpunkt",
    type: "column",
    data: countData,
  };
}

function buildWeeklySeries(cfg) {
  const levels = cfg.chartOptions.yaxis[0].labels.levels;
  const seriesByLabel = {};
  Object.values(levels).forEach((label) => {
    seriesByLabel[label] = {};
  });
  cfg.series.forEach((serie) => {
    serie.data.forEach((d) => {
      const week = moment(d.x).startOf("isoWeek").format("YYYY-MM-DD");
      const label = levels[d.y];
      if (!label) return;
      seriesByLabel[label][week] = (seriesByLabel[label][week] || 0) + 1;
    });
  });
  return Object.keys(seriesByLabel).map((label) => ({
    name: label,
    data: Object.entries(seriesByLabel[label]).map(([week, count]) => ({
      x: week,
      y: count,
    })),
  }));
}

function replaceDataWithPoints(series) {
  return series.map((s) => {
    if (s.data_points) {
      return {
        ...s,
        data: s.data_points.map((dp) => ({ x: dp.x, y: dp.y })),
      };
    }
    return s;
  });
}

function makeGlobalAverageLine(series, name = "Viktat medelvärde") {
  if (!Array.isArray(series) || series.length === 0) return null;
  const baseSeries = series[0];
  if (!baseSeries.data) return null;
  const valid = baseSeries.data.filter((p) => p.y !== null && !isNaN(p.y) && p.y !== 0);
  if (valid.length === 0) return null;
  const sum = valid.reduce((acc, p) => acc + p.y, 0);
  const globalAvg = sum / valid.length;
  const xVals = [...new Set(baseSeries.data.map((p) => p.x))];
  return {
    name,
    type: "line",
    data: xVals.map((x) => ({ x, y: globalAvg })),
    color: "#FF0000",
    yAxisIndex: baseSeries.yAxisIndex ?? 0,
    stroke: { width: 2, dashArray: 4 },
  };
}

function slugify(s = "") {
  return String(s)
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const BASE_TEMPLATE = Object.freeze({
  heading: "Resultatutveckling över tid",
  type: "line",
  series: [
    {
      name: "Alla i Järfälla",
      data: [
        { x: "2025-02-01", y: 3.5 },
        { x: "2025-03-01", y: 3.8 },
        { x: "2025-04-01", y: 3.2 },
        { x: "2025-05-01", y: 3.7 },
        { x: "2025-06-01", y: 4.0 },
        { x: "2025-07-01", y: 3.9 },
        { x: "2025-08-01", y: 4.1 },
        { x: "2025-09-01", y: 3.8 },
        { x: "2025-10-01", y: 3.6 },
        { x: "2025-11-01", y: 3.4 },
        { x: "2025-12-01", y: 3.5 },
      ],
    },
    {
      name: "Dina svar",
      data: [
        { x: "2025-02-01", y: 4 },
        { x: "2025-03-01", y: 2 },
        { x: "2025-04-01", y: 3 },
        { x: "2025-05-01", y: 3 },
        { x: "2025-06-01", y: 4 },
        { x: "2025-07-01", y: null },
        { x: "2025-08-01", y: 4 },
        { x: "2025-09-01", y: 5 },
        { x: "2025-10-01", y: 5 },
        { x: "2025-11-01", y: 4 },
        { x: "2025-12-01", y: 5 },
      ],
    },
  ],
  chartOptions: {
    chart: {
      id: "calls-over-time",
      type: "line",
      zoom: { enabled: false },
      foreColor: "#777777",
    },
    colors: [
      "#008FFB",
      "#FFD700",
      "#FF4560",
      "#00E396",
      "#775DD0",
      "#FEB019",
      "#546E7A",
      "#26A69A",
      "#D10CE8",
      "#F86624",
      "#2E294E",
      "#90EE7E",
      "#FF9F43",
      "#1E90FF",
      "#E91E63",
    ],
    stroke: { curve: "smooth", dashArray: [0, 0, 5] },
    legend: { labels: { colors: "#777777" } },
    tooltip: { enabled: false, shared: false, followCursor: true, theme: "dark" },
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      title: { text: "Tid", style: { color: "#777777" } },
      labels: { style: { color: "#777777" } },
    },
    yaxis: {
      title: { text: "Välmående", style: { color: "#777777" } },
      min: 1,
      max: 5,
      tickAmount: 4,
      labels: {
        style: { color: "#777777" },
        formatter(val) {
          const levels = {
            1: "Mycket dålig",
            2: "Ganska dålig",
            3: "Neutral",
            4: "Ganska bra",
            5: "Mycket bra",
          };
          return levels[val] || val;
        },
      },
    },
  },
});

export default {
  name: "SurveyCharts",
  props: {
    /**
     * Array av rapportobjekt (tidigare this.fetchedReports)
     */
    reports: { type: Array, required: true },

    /**
     * Överskriv grundmallen om du vill (annars används BASE_TEMPLATE)
     */
    baseTemplate: { type: Object, default: () => BASE_TEMPLATE },
    noGrid: { type: Boolean, default: false },
  },

  computed: {
    chartModels() {
      return (this.reports || []).map((r, idx) => {
        let base = JSON.parse(JSON.stringify(this.baseTemplate));
        let cfg = JSON.parse(JSON.stringify(deepMerge(base, r)));

        // Normalisera y-axel enligt levels
        cfg.chartOptions.yaxis = normalizeYAxis(cfg.chartOptions.yaxis);

        // Stabilt id + metadata
        cfg.chartOptions.id = r.id || `${slugify(r.title || "chart")}-${idx}`;
        cfg.heading = JSON.parse(JSON.stringify(cfg.chartOptions.yaxis.title.text));
        cfg.format = JSON.parse(JSON.stringify(cfg.chartOptions.yaxis.title.format));

        // Konvertera data_points → data
        cfg.series = replaceDataWithPoints(cfg.series);

        const { cleaned, average, pointCount } = makeAverageSeries(cfg.series);
        const countSeries = addCountSeries(cleaned);
        const globalAvgLine = makeGlobalAverageLine(cleaned);

        // Multi-axis setup (vänster: värden, höger: count)
        cfg.chartOptions.yaxis = [
          { ...cfg.chartOptions.yaxis },
          {
            opposite: true,
            title: { text: "" },
            min: 0,
            labels: {
              formatter(val) {
                return Number.isInteger(val) ? val : "";
              },
            },
          },
          {
            yAxisIndex: 0,
            show: false,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
            title: { text: undefined },
          },
        ];
        cfg.chartOptions.yaxis[0].title.text = undefined; // rubriken på vänster y‑axel borta
        cfg.pointCount = pointCount;

        if (cfg.format === "SingleNumeric" || cfg.format === "integer") {
          cfg.series = [average, countSeries];
          cfg.type = "line"; // behåll line för medelvärde (count ritas som column)
          cfg.chartOptions.plotOptions = {
            bar: { columnWidth: "40%", borderRadius: 2 },
          };
          cfg.series.forEach((s) => {
            if (s.name === "Medelvärde") s.yAxisIndex = 0;
            else if (s.name.includes("Antal")) s.yAxisIndex = 1;
          });
          if (globalAvgLine) {
            // Valfritt: lägg till global medellinje
            // cfg.series.push(globalAvgLine);
          }
        } else if (cfg.format === "MultiNumeric") {
          cfg.type = "bar";
          cfg.series = buildWeeklySeries(cfg);
          cfg.chartOptions = deepMerge(cfg.chartOptions, {
            chart: { type: "bar", stacked: true, foreColor: "#777" },
            xaxis: { type: "datetime", title: { text: "Vecka" } },
            yaxis: { title: { text: undefined }, labels: { show: false } },
            legend: {
              position: "bottom",
              horizontalAlign: "left",
              floating: false,
              fontSize: "13px",
              markers: { width: 12, height: 12 },
              itemMargin: { horizontal: 15, vertical: 0 },
            },
          });
        }

        return cfg;
      });
    },
  },
};
</script>

<style scoped>
.charts {
  display: grid;
  grid-gap: 1.5rem;
}
</style>
